import React from 'react';
import { View, StyleProp, ViewStyle } from 'react-native';
import { Text } from '@/components/Text';
import { styles } from './styles';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/therapist/Button';
import { IS_WEB } from '@/utils/platform';

interface CardProps {
  title?: string;
  icon?: React.ComponentProps<typeof Icon>['name'];
  iconPosition?: 'inside' | 'outside';
  linkText?: string;
  onLinkPress?: () => void;
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  styleBody?: StyleProp<ViewStyle>;
}

const Card: React.FC<CardProps> = ({
  title,
  icon,
  iconPosition = 'inside',
  linkText,
  onLinkPress,
  children,
  style,
  styleBody,
}) => {
  const isIconInside = icon && iconPosition === 'inside';
  const isIconOutside = icon && iconPosition === 'outside';
  const showLink = !!(linkText && onLinkPress);
  const hasHeader = !!(title || icon || linkText);

  const renderIcon = () =>
    icon ? <Icon name={icon} style={styles.icon} /> : null;

  const renderLink = () =>
    showLink ? (
      <Button onPress={onLinkPress!} label={linkText!} look="tertiary" />
    ) : null;

  const renderHeaderWithOutsideIcon = () => (
    <View style={styles.headerRow}>
      <View style={styles.iconBox}>{renderIcon()}</View>
      <View style={styles.titleAndLinkContainer}>
        {title && <Text style={styles.title}>{title}</Text>}
        {renderLink()}
      </View>
    </View>
  );

  const renderHeaderWithInsideIcon = () => (
    <View style={styles.insideHeaderContainer}>
      <View style={styles.headerRow}>
        <View style={styles.insideHeaderTitleGroup}>
          {isIconInside && (
            <View style={styles.inlineIcon}>{renderIcon()}</View>
          )}
          {title && (
            <Text
              style={[
                styles.title,
                IS_WEB
                  ? ({ wordBreak: 'break-word', flexShrink: 1 } as any)
                  : null,
              ]}
            >
              {title}
            </Text>
          )}
        </View>
        {renderLink()}
      </View>
    </View>
  );

  return (
    <View style={[styles.cardContainer, style]}>
      {hasHeader &&
        (isIconOutside
          ? renderHeaderWithOutsideIcon()
          : renderHeaderWithInsideIcon())}
      <View
        style={[
          styles.cardBody,
          isIconOutside && styles.cardBodyRoundedTopLeft,
          styleBody,
        ]}
      >
        {children}
      </View>
    </View>
  );
};

export default Card;
